import type { ComponentPropsWithRef, ReactNode, RefAttributes } from "react";
import type {
	DialogProps as AriaDialogProps,
	ModalOverlayProps as AriaModalOverlayProps,
	ModalRenderProps as AriaModalRenderProps,
} from "react-aria-components";
import {
	Dialog as AriaDialog,
	DialogTrigger as AriaDialogTrigger,
	Modal as AriaModal,
	ModalOverlay as AriaModalOverlay,
} from "react-aria-components";
import { cx } from "@/utils/cx";
import { CloseButton } from "./close-button";

interface ModalOverlayProps
	extends AriaModalOverlayProps,
		RefAttributes<HTMLDivElement> {}

export const ModalOverlay = (props: ModalOverlayProps) => (
	<AriaModalOverlay
		{...props}
		className={(state) =>
			cx(
				"fixed inset-0 flex min-h-dvh w-full items-center justify-end bg-overlay/70 pl-6 outline-hidden ease-linear md:pl-10",
				!!state.isEntering && "fade-in animate-in duration-300",
				!!state.isExiting && "fade-out animate-out duration-500",
				typeof props.className === "function"
					? props.className(state)
					: props.className
			)
		}
	/>
);
ModalOverlay.displayName = "ModalOverlay";

interface ModalProps
	extends AriaModalOverlayProps,
		RefAttributes<HTMLDivElement> {}

export const Modal = (props: ModalProps) => (
	<AriaModal
		{...props}
		className={(state) =>
			cx(
				"inset-y-0 right-0 h-full w-full max-w-100 shadow-xl transition",
				!!state.isEntering && "slide-in-from-right animate-in duration-300",
				!!state.isExiting && "slide-out-to-right animate-out duration-500",
				typeof props.className === "function"
					? props.className(state)
					: props.className
			)
		}
	/>
);
Modal.displayName = "Modal";

interface DialogProps extends AriaDialogProps, RefAttributes<HTMLElement> {}

export const Dialog = (props: DialogProps) => (
	<AriaDialog
		aria-label={
			props["aria-label"] ?? (props["aria-labelledby"] ? undefined : "Painel")
		}
		role="dialog"
		{...props}
		className={cx(
			"relative flex size-full flex-col overflow-hidden bg-primary outline-hidden ring-1 ring-secondary_alt",
			props.className
		)}
	/>
);
Dialog.displayName = "Dialog";

interface SlideoutMenuProps
	extends Omit<AriaModalOverlayProps, "children">,
		RefAttributes<HTMLDivElement> {
	children:
		| ReactNode
		| ((children: AriaModalRenderProps & { close: () => void }) => ReactNode);
	dialogClassName?: string;
}

const Menu = ({
	children,
	dialogClassName,
	className,
	...props
}: SlideoutMenuProps) => (
	<ModalOverlay {...props}>
		<Modal
			className={(state) =>
				cx(
					typeof className === "function"
						? className(state)
						: (className ?? undefined)
				)
			}
		>
			{(state) => (
				<Dialog className={dialogClassName}>
					{({ close }) => {
						if (typeof children === "function") {
							return children({ ...state, close });
						}
						return children;
					}}
				</Dialog>
			)}
		</Modal>
	</ModalOverlay>
);
Menu.displayName = "SlideoutMenu";

const Content = ({ role = "main", ...props }: ComponentPropsWithRef<"div">) => (
	<div
		role={role}
		{...props}
		className={cx(
			"flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto overscroll-auto px-4 md:px-6",
			props.className
		)}
	/>
);
Content.displayName = "SlideoutContent";

interface SlideoutHeaderProps extends ComponentPropsWithRef<"header"> {
	onClose?: () => void;
}

const Header = ({
	className,
	children,
	onClose,
	...props
}: SlideoutHeaderProps) => (
	<header
		{...props}
		className={cx(
			"relative z-1 w-full shrink-0 bg-primary px-4 pt-6 md:px-6",
			className
		)}
	>
		{children}
		<CloseButton
			className="absolute top-3 right-3 shrink-0"
			onClick={onClose}
			size="md"
		/>
	</header>
);
Header.displayName = "SlideoutHeader";

const Footer = (props: ComponentPropsWithRef<"footer">) => (
	<footer
		{...props}
		className={cx(
			"mt-auto w-full shrink-0 bg-primary p-4 shadow-[inset_0px_1px_0px_0px] shadow-border-secondary md:px-6",
			props.className
		)}
	/>
);
Footer.displayName = "SlideoutFooter";

const SlideoutMenu = Menu as typeof Menu & {
	Trigger: typeof AriaDialogTrigger;
	Content: typeof Content;
	Header: typeof Header;
	Footer: typeof Footer;
};
SlideoutMenu.displayName = "SlideoutMenu";

SlideoutMenu.Trigger = AriaDialogTrigger;
SlideoutMenu.Content = Content;
SlideoutMenu.Header = Header;
SlideoutMenu.Footer = Footer;

export { SlideoutMenu };
